// Pitt Gabruz Cricket — broadcast-grade portrait flyer (Phase 4).
// Design language: Big Bash / RCB energy.
//   - 9:16 portrait (story / phone share)
//   - Red #dc2626 + black ground + white type
//   - Bold italic Anton display, Inter body, JetBrains Mono numerals
//   - Diagonal red energy band across the top
//   - Striped red/black reporting footer
//   - Captain / keeper marked with bold inline badges

import React from "react";
import {
  Calendar, Clock, MapPin, Shield, Star
} from "lucide-react";
import { SKILL_BY_CODE } from "../../data";
import {
  playerSkillCodes, formatDateTemplate, formatTimeTemplate
} from "./flyerHelpers";

function roleLabel(codes, isKeeper) {
  if (isKeeper) return "WICKET-KEEPER";
  const cats = new Set((codes || []).map(c => SKILL_BY_CODE[c]?.category).filter(Boolean));
  const bat  = cats.has("BAT");
  const bowl = cats.has("BOWL_PACE") || cats.has("BOWL_SPIN");
  if (cats.has("ALL") || (bat && bowl)) return "ALL-ROUNDER";
  if (cats.has("BOWL_PACE"))            return "PACE BOWLER";
  if (cats.has("BOWL_SPIN"))            return "SPIN BOWLER";
  if (bat)                              return "BATTER";
  return "PLAYER";
}

function franchiseName(team) {
  return (team?.name || "Pitt Gabruz")
    .replace(/\b(Cricket|Volleyball|Team|Club)\b/gi, "")
    .replace(/\s+/g, " ")
    .trim()
    .toUpperCase() || "PITT GABRUZ";
}

export function PittGabruzCricketFlyer({
  match, opp, team, startingPlayers, reportingTime,
  venueShort, captainPickId, wicketKeeperId
}) {
  const home = franchiseName(team);
  const away = (opp || "OPPONENT").toUpperCase();

  return (
    <div className="pgc-bash">
      {/* ─── Watermark logo (sits behind all content) ─── */}
      <img
        className="pgc-bash-watermark"
        src="/logo.png"
        alt=""
        aria-hidden="true"
        crossOrigin="anonymous"
        onError={e => e.currentTarget.style.display = "none"}
      />

      {/* ─── Decorative slash bars (red energy strokes top + bottom) ─── */}
      <div className="pgc-bash-slash pgc-bash-slash--tl"/>
      <div className="pgc-bash-slash pgc-bash-slash--br"/>

      {/* ─── HEADER (corner crest removed — watermark carries the brand) ─── */}
      <header className="pgc-bash-head">
        <div className="pgc-bash-head-text">
          <p className="pgc-bash-league">
            <span/> PITTSBURGH PREMIER CRICKET LEAGUE
          </p>
          <h1 className="pgc-bash-brand">GABRUZ UNIVERSE</h1>
        </div>
      </header>

      {/* ─── MATCH HERO ─── */}
      <section className="pgc-bash-hero">
        <div className="pgc-bash-team pgc-bash-team--home">
          <small>HOME</small>
          <strong>{home}</strong>
        </div>
        <div className="pgc-bash-vs">
          <span className="pgc-bash-vs-line"/>
          <em>VS</em>
          <span className="pgc-bash-vs-line"/>
        </div>
        <div className="pgc-bash-team pgc-bash-team--away">
          <small>AWAY</small>
          <strong>{away}</strong>
        </div>
      </section>

      {/* ─── MATCH INFO STRIP ─── */}
      <div className="pgc-bash-info">
        <div className="pgc-bash-info-cell">
          <Calendar size={13}/>
          <small>DATE</small>
          <b>{formatDateTemplate(match?.date)}</b>
        </div>
        <div className="pgc-bash-info-cell">
          <Clock size={13}/>
          <small>FIRST BALL</small>
          <b>{formatTimeTemplate(match?.time || reportingTime)}</b>
        </div>
        <div className="pgc-bash-info-cell">
          <MapPin size={13}/>
          <small>GROUND</small>
          <b>{venueShort}</b>
        </div>
      </div>

      {/* ─── PLAYER LIST (no section header — content is self-evident) ─── */}
      <ol className="pgc-bash-xi">
        {startingPlayers.map((p, i) => {
          const codes = playerSkillCodes(team, p.id);
          const isCaptain = !!p.id && p.id === captainPickId;
          const isKeeper  = !!p.id && p.id === wicketKeeperId;
          const role = roleLabel(codes, isKeeper);
          const cls = [
            "pgc-bash-row",
            isCaptain && "pgc-bash-row--c",
            isKeeper  && "pgc-bash-row--wk"
          ].filter(Boolean).join(" ");
          return (
            <li key={p.id || i} className={cls}>
              <span className="pgc-bash-row-bar"/>
              <span className="pgc-bash-num">{String(i + 1).padStart(2, "0")}</span>
              <div className="pgc-bash-row-body">
                <strong>{p.name}</strong>
                <small>{role}</small>
              </div>
              {isCaptain && (
                <span className="pgc-bash-badge pgc-bash-badge--c">
                  <Star size={9} fill="currentColor" stroke="none"/> C
                </span>
              )}
              {isKeeper && !isCaptain && (
                <span className="pgc-bash-badge pgc-bash-badge--wk">
                  <Shield size={9}/> WK
                </span>
              )}
            </li>
          );
        })}
      </ol>

      {/* ─── STRIPED REPORTING FOOTER ─── */}
      <footer className="pgc-bash-foot">
        <div className="pgc-bash-foot-stripes"/>
        <div className="pgc-bash-foot-text">
          <small>REPORT BY</small>
          <strong>{formatTimeTemplate(reportingTime)}</strong>
        </div>
      </footer>
    </div>
  );
}
